import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import FamilyRestroomRoundedIcon from '@mui/icons-material/FamilyRestroomRounded';
import {
  authService,
  RegisterRole,
  PASSWORD_RULE,
  PASSWORD_RULE_MESSAGE,
  PASSWORD_BLOCKLISTED_MESSAGE,
  isPasswordBlocklisted,
} from '../api/auth';
import { colors } from '../../../theme';

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  role: RegisterRole;
  acceptedTerms: boolean;
  acceptedPrivacy: boolean;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  acceptedTerms?: string;
  acceptedPrivacy?: string;
}

const ROLE_OPTIONS: Array<{ value: RegisterRole; label: string; icon: React.ReactElement }> = [
  { value: 'PARENT', label: 'Padre/Madre', icon: <FamilyRestroomRoundedIcon fontSize="small" /> },
  { value: 'TEACHER', label: 'Profesor', icon: <SchoolRoundedIcon fontSize="small" /> },
];

const toggleSx = {
  py: 1.3,
  px: 1,
  textTransform: 'none' as const,
  fontWeight: 700,
  fontSize: '0.8rem',
  gap: 0.7,
  '&.Mui-selected': {
    bgcolor: `${colors.primary.lightest} !important`,
    color: `${colors.primary.main} !important`,
    borderColor: `${colors.primary.main} !important`,
  },
};

/**
 * The account type this signup starts with.
 *
 * The teacher landing page (ADR-007 S2.1 / TCH-01) links here as
 * `/register?role=TEACHER`: its acceptance criteria require that
 * teacher-oriented links do not route the candidate through the
 * learner/guardian journey, and this form defaults to PARENT.
 *
 * An unknown or absent value falls back to that default rather than erroring —
 * the parameter is a hint from a link, not input to trust, and it is still only
 * a preselection the visitor can change.
 */
const initialRole = (requested: string | null): RegisterRole => {
  const wanted = (requested || '').toUpperCase();
  const known = ROLE_OPTIONS.find((opt) => opt.value === wanted);
  return known ? known.value : 'PARENT';
};

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [form, setForm] = useState<FormState>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: initialRole(searchParams.get('role')),
    acceptedTerms: false,
    acceptedPrivacy: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [apiError, setApiError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
    setApiError('');
  };

  const handleCheck =
    (name: 'acceptedTerms' | 'acceptedPrivacy') =>
    (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setForm((prev) => ({ ...prev, [name]: checked }));
      setErrors((prev) => ({ ...prev, [name]: undefined }));
      setApiError('');
    };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!form.firstName.trim()) newErrors.firstName = 'Ingresa tu nombre';
    if (!form.lastName.trim()) newErrors.lastName = 'Ingresa tus apellidos';
    if (!form.email.trim()) {
      newErrors.email = 'Ingresa tu correo';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'Ingresa un correo válido';
    }
    if (!form.password) {
      newErrors.password = 'Ingresa una contraseña';
    } else if (!PASSWORD_RULE.test(form.password)) {
      // Mirrors the backend rule so the user is not bounced by a server 400.
      newErrors.password = PASSWORD_RULE_MESSAGE;
    }
    if (!form.confirmPassword) {
      newErrors.confirmPassword = 'Confirma tu contraseña';
    } else if (form.password !== form.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }
    if (!form.acceptedTerms) newErrors.acceptedTerms = 'Debes aceptar los Términos del Servicio';
    if (!form.acceptedPrivacy) {
      newErrors.acceptedPrivacy = 'Debes aceptar el Aviso de Privacidad';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setApiError('');

    try {
      await authService.register({
        email: form.email.trim(),
        password: form.password,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        role: form.role,
        acceptedTerms: form.acceptedTerms,
        acceptedPrivacy: form.acceptedPrivacy,
      });
      // The account exists but is unconfirmed until the emailed code is entered.
      navigate('/verify-email', { state: { email: form.email.trim() }, replace: true });
    } catch (err: any) {
      if (isPasswordBlocklisted(err)) {
        // TCH-02-FR-03: a field problem, so it goes on the field.
        setErrors((prev) => ({ ...prev, password: PASSWORD_BLOCKLISTED_MESSAGE }));
        return;
      }
      setApiError(err?.message || 'No pudimos crear tu cuenta. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 140px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f7fb',
        py: 6,
        px: 2,
      }}
    >
      <Card
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 460,
          borderRadius: 4,
          border: `1px solid ${colors.grey[200]}`,
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          {/* Logo */}
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 900, color: colors.primary.main }}>
              IQMaxx
            </Typography>
          </Box>

          {/* Heading */}
          <Typography variant="h5" sx={{ fontWeight: 800, textAlign: 'center', mb: 0.5 }}>
            Crea tu cuenta
          </Typography>
          <Typography
            variant="body2"
            sx={{ textAlign: 'center', color: colors.text.secondary, mb: 3 }}
          >
            Clases en vivo con profesores verificados
          </Typography>

          {/* API error */}
          {apiError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {apiError}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            {/* Role selector */}
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: colors.text.secondary, mb: 1, display: 'block' }}
            >
              Soy…
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={form.role}
              onChange={(_, val) => val && setForm((prev) => ({ ...prev, role: val }))}
              fullWidth
              sx={{ mb: 3 }}
              aria-label="Tipo de cuenta"
            >
              {ROLE_OPTIONS.map((opt) => (
                <ToggleButton key={opt.value} value={opt.value} sx={toggleSx}>
                  {opt.icon}
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            {/* Name row */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                label="Nombre"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                error={!!errors.firstName}
                helperText={errors.firstName}
                fullWidth
                size="small"
                autoComplete="given-name"
              />
              <TextField
                label="Apellidos"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                error={!!errors.lastName}
                helperText={errors.lastName}
                fullWidth
                size="small"
                autoComplete="family-name"
              />
            </Box>

            {/* Email */}
            <TextField
              label="Correo electrónico"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              autoComplete="email"
            />

            {/* Password */}
            <TextField
              label="Contraseña"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={form.password}
              onChange={handleChange}
              error={!!errors.password}
              helperText={errors.password || PASSWORD_RULE_MESSAGE}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    >
                      {showPassword ? (
                        <VisibilityOffRoundedIcon fontSize="small" />
                      ) : (
                        <VisibilityRoundedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Confirm Password */}
            <TextField
              label="Confirmar contraseña"
              name="confirmPassword"
              type={showConfirm ? 'text' : 'password'}
              value={form.confirmPassword}
              onChange={handleChange}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowConfirm((v) => !v)}
                      edge="end"
                      aria-label={showConfirm ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    >
                      {showConfirm ? (
                        <VisibilityOffRoundedIcon fontSize="small" />
                      ) : (
                        <VisibilityRoundedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Terms + privacy — required by the backend and by TCH-02-FR-02 */}
            <Box sx={{ mb: 2.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={form.acceptedTerms}
                    onChange={handleCheck('acceptedTerms')}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                    Acepto los{' '}
                    <Link to="/terms" target="_blank" rel="noopener noreferrer">
                      Términos del Servicio
                    </Link>
                  </Typography>
                }
              />
              {errors.acceptedTerms && (
                <FormHelperText error sx={{ ml: 4, mt: -0.5 }}>
                  {errors.acceptedTerms}
                </FormHelperText>
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={form.acceptedPrivacy}
                    onChange={handleCheck('acceptedPrivacy')}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                    Acepto el{' '}
                    <Link to="/privacy" target="_blank" rel="noopener noreferrer">
                      Aviso de Privacidad
                    </Link>
                  </Typography>
                }
              />
              {errors.acceptedPrivacy && (
                <FormHelperText error sx={{ ml: 4, mt: -0.5 }}>
                  {errors.acceptedPrivacy}
                </FormHelperText>
              )}
            </Box>

            {/* Submit */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                bgcolor: colors.primary.main,
                color: 'white',
                fontWeight: 700,
                borderRadius: 2,
                py: 1.3,
                textTransform: 'none',
                fontSize: '0.95rem',
                boxShadow: '0 4px 16px rgba(74,144,226,0.35)',
                '&:hover': { bgcolor: colors.primary.dark },
              }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Crear cuenta'}
            </Button>
          </Box>

          {/* Footer links */}
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              ¿Ya tienes cuenta?{' '}
              <Box
                component={Link}
                to="/login"
                sx={{
                  color: colors.primary.main,
                  fontWeight: 700,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Inicia sesión
              </Box>
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Box
                component={Link}
                to="/auth"
                sx={{
                  color: colors.text.secondary,
                  fontSize: '0.8rem',
                  textDecoration: 'none',
                  '&:hover': { color: colors.primary.main },
                }}
              >
                ← Volver a las opciones de registro
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Register;
